import React from "react";
import {
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tooltip,
} from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import type { SortDirection } from "../../hooks/useListState";

export interface SortOption {
    /** Field name used for sorting */
    field: string;
    /** Display label for the option */
    label: string;
}

export interface SortBySelectProps {
    /** Available sort options */
    options: SortOption[];
    /** Currently selected sort field */
    sortBy: string;
    /** Current sort direction */
    sortDirection: SortDirection;
    /** Callback when sort field changes */
    onSortChange: (field: string) => void;
    /** Callback when sort direction is toggled */
    onDirectionToggle: () => void;
}

/**
 * Sort-by select dropdown with direction toggle button.
 * Provides a compact UI for selecting sort field and direction.
 */
const SortBySelect: React.FC<SortBySelectProps> = ({
    options,
    sortBy,
    sortDirection,
    onSortChange,
    onDirectionToggle,
}) => {
    if (options.length === 0) {
        return null;
    }

    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="sort-by-label">Sort by</InputLabel>
                <Select
                    labelId="sort-by-label"
                    label="Sort by"
                    value={sortBy}
                    onChange={(e) => onSortChange(e.target.value)}
                >
                    {options.map((option) => (
                        <MenuItem key={option.field} value={option.field}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Tooltip title={sortDirection === "asc" ? "Ascending" : "Descending"}>
                <IconButton
                    size="small"
                    onClick={onDirectionToggle}
                    sx={{ color: "primary.main" }}
                    aria-label={`Sort ${sortDirection === "asc" ? "ascending" : "descending"}`}
                >
                    {sortDirection === "asc" ? (
                        <ArrowUpward fontSize="small" />
                    ) : (
                        <ArrowDownward fontSize="small" />
                    )}
                </IconButton>
            </Tooltip>
        </Stack>
    );
};

export default SortBySelect;

